<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import conversationChecklistItemsAPI from 'dashboard/api/conversationChecklistItems';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  kanbanCard: {
    type: Object,
    default: null,
  },
  pipelineId: {
    type: [String, Number],
    required: true,
  },
});

const emit = defineEmits(['update-conversation']);

const { t } = useI18n();

// State
const newItem = ref('');
const checklistItems = ref([]);
const isSubmitting = ref(false);
const loading = ref(false);

// Computed
const completedCount = computed(() => {
  return checklistItems.value.filter(item => item.completed).length;
});

const totalCount = computed(() => {
  return checklistItems.value.length;
});

const progressPercentage = computed(() => {
  if (totalCount.value === 0) return 0;
  return Math.round((completedCount.value / totalCount.value) * 100);
});

// Removido mock data - usando dados reais do backend

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMinutes = Math.floor((now - date) / (1000 * 60));
  
  if (diffInMinutes < 1) return 'agora';
  if (diffInMinutes < 60) return `${diffInMinutes}m atrás`;
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h atrás`;
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return `${diffInDays}d atrás`;
  
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const addItem = async () => {
  if (!newItem.value.trim() || isSubmitting.value) return;
  
  isSubmitting.value = true;
  
  try {
    const response = await conversationChecklistItemsAPI.create(props.conversation.id, {
      title: newItem.value,
      pipeline_id: props.pipelineId,
    });
    
    checklistItems.value.push(response.data);
    newItem.value = '';
    
    // Emit update event with checklist stats
    emit('update-conversation', { 
      ...props.conversation,
      checklist_stats: {
        completed: completedCount.value,
        total: totalCount.value,
        percentage: progressPercentage.value
      }
    });
    
  } catch (error) {
    console.error('Error adding checklist item:', error);
    // TODO: Show error toast
  } finally {
    isSubmitting.value = false;
  }
};

const toggleItem = async (item) => {
  const originalState = item.completed;
  const originalCompletedAt = item.completed_at;
  
  try {
    // Optimistic update
    item.completed = !item.completed;
    item.completed_at = item.completed ? new Date().toISOString() : null;
    
    await conversationChecklistItemsAPI.toggle(props.conversation.id, item.id);
    
    // Emit update event with updated checklist stats
    emit('update-conversation', { 
      ...props.conversation,
      checklist_stats: {
        completed: completedCount.value,
        total: totalCount.value,
        percentage: progressPercentage.value
      }
    });
    
  } catch (error) {
    // Revert on error
    item.completed = originalState;
    item.completed_at = originalCompletedAt;
    console.error('Error toggling checklist item:', error);
    // TODO: Show error toast
  }
};

const deleteItem = async (itemId) => {
  const itemIndex = checklistItems.value.findIndex(item => item.id === itemId);
  if (itemIndex === -1) return;
  
  const backupItem = checklistItems.value[itemIndex];
  
  try {
    // Optimistic update
    checklistItems.value.splice(itemIndex, 1);
    
    await conversationChecklistItemsAPI.delete(props.conversation.id, itemId);
    
    // Emit update event with updated checklist stats
    emit('update-conversation', { 
      ...props.conversation,
      checklist_stats: {
        completed: completedCount.value,
        total: totalCount.value,
        percentage: progressPercentage.value
      }
    });
    
  } catch (error) {
    // Revert on error
    checklistItems.value.splice(itemIndex, 0, backupItem);
    console.error('Error deleting checklist item:', error);
    // TODO: Show error toast
  }
};

const handleKeydown = (event) => {
  if (event.key === 'Enter') {
    event.preventDefault();
    addItem();
  }
};

const loadChecklistItems = async () => {
  loading.value = true;
  try {
    // Se temos um kanbanCard, usar dados específicos do card
    // Senão, usar checklist da conversa filtrado por pipeline
    if (props.kanbanCard) {
      // Para cards específicos, carregar checklist da conversa filtrado por pipeline
      // Os itens já são específicos por pipeline no backend
      const response = await conversationChecklistItemsAPI.get(props.conversation.id, {
        pipeline_id: props.pipelineId
      });
      checklistItems.value = response.data || [];
    } else {
      // Fallback para conversas sem card específico
      const response = await conversationChecklistItemsAPI.get(props.conversation.id, {
        pipeline_id: props.pipelineId
      });
      checklistItems.value = response.data || [];
    }
  } catch (error) {
    console.error('Error loading checklist items:', error);
    checklistItems.value = [];
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  loadChecklistItems();
});
</script>

<template>
  <div class="bg-n-background rounded-lg border border-n-weak hover:shadow-md transition-shadow">
    <div class="p-4 space-y-4">
      <!-- Progress Bar -->
      <div v-if="totalCount > 0" class="mb-4">
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium text-n-slate-12">Progresso do Checklist</span>
          <span class="text-sm text-n-slate-11">{{ completedCount }}/{{ totalCount }}</span>
        </div>
        
        <div class="w-full bg-n-slate-2 rounded-full h-2">
          <div
            class="bg-green-500 h-2 rounded-full transition-all duration-300"
            :style="{ width: `${progressPercentage}%` }"
          />
        </div>
      </div>
      
      <!-- Add Item Form -->
      <div class="flex gap-3">
        <div class="flex-1">
          <input
            v-model="newItem"
            type="text"
            placeholder="Adicionar novo item..."
            class="w-full px-3 py-2 border border-n-weak rounded-md focus:border-n-blue-11 focus:ring-2 focus:ring-n-blue-11/20 focus:outline-none transition-all text-sm"
            @keydown="handleKeydown"
            :disabled="isSubmitting"
          />
        </div>
        
        <button
          @click="addItem"
          :disabled="!newItem.trim() || isSubmitting"
          class="px-3 py-2 bg-n-blue-11 text-white rounded-md hover:bg-n-blue-12 disabled:bg-n-slate-6 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm"
        >
          <i v-if="isSubmitting" class="i-lucide-loader-2 size-4 animate-spin" />
          <i v-else class="i-lucide-plus size-4" />
          <span class="hidden sm:inline">Adicionar</span>
        </button>
      </div>
      
      <!-- Checklist Items -->
      <div class="space-y-3">
        <div v-if="!checklistItems.length && !loading" class="text-center py-8">
          <div class="w-16 h-16 mx-auto mb-4 bg-n-slate-2 rounded-full flex items-center justify-center">
            <i class="i-lucide-check-square size-8 text-n-slate-11" />
          </div>
          <p class="text-n-slate-11">Nenhum item no checklist</p>
        </div>
        
        <div
          v-for="item in checklistItems"
          :key="item.id"
          class="group flex items-center space-x-3 p-2 rounded-lg hover:bg-n-slate-2 transition-colors"
        >
          <button
            @click="toggleItem(item)"
            class="w-5 h-5 rounded border-2 transition-all flex items-center justify-center"
            :class="item.completed 
              ? 'bg-green-500 border-green-500 text-white' 
              : 'border-gray-300 hover:border-green-500 bg-white'"
          >
            <i v-if="item.completed" class="i-lucide-check size-3" />
          </button>
          
          <label
            class="text-sm flex-1 cursor-pointer transition-all"
            :class="item.completed ? 'line-through text-n-slate-11' : 'text-n-slate-12'"
          >
            {{ item.title }}
          </label>
          
          <button
            @click="deleteItem(item.id)"
            class="p-1 text-n-slate-9 hover:text-red-600 hover:bg-red-50 rounded transition-colors opacity-0 group-hover:opacity-100"
            title="Excluir item"
          >
            <i class="i-lucide-trash-2 size-4" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Checkbox animation */
.group button[type="button"]:hover {
  transform: scale(1.05);
}

/* Progress bar animation */
.bg-green-500 {
  transition: width 0.3s ease-in-out;
}
</style>
